import type {
  ChannelDef,
  DatumDef,
  Field,
  FieldDefBase,
  SecondaryFieldDef,
  TypedFieldDef,
} from "vega-lite/types_unstable/channeldef.js";

import { VegaLiteSpec, VegaSpec } from "./types.js";
import { isVegaLiteSpec } from "./vegaSpecVersionTransformer.js";

// Copied from VegaLite because can't import it directly in our Node.JS builds unfortunately
export function isFieldDef<F extends Field>(
  channelDef: Partial<ChannelDef<F>> | FieldDefBase<F> | DatumDef<F, any>,
): channelDef is FieldDefBase<F> | TypedFieldDef<F> | SecondaryFieldDef<F> {
  return (
    !!channelDef &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (!!(channelDef as any)["field"] ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (channelDef as any)["aggregate"] === "count")
  );
}

export function vegaStringExpr(s: string): string {
  // Vega's parser still treats these JSON-valid characters as line terminators.
  return JSON.stringify(s)
    .replaceAll("\u2028", "\\u2028")
    .replaceAll("\u2029", "\\u2029");
}

/**
 * Escapes a string for use inside a single-quoted Vega expression string
 * literal. Backslashes and single quotes are escaped so the value cannot
 * terminate the literal; line breaks are escaped because Vega's tokenizer
 * rejects raw ones.
 */
export function escapeVegaSingleQuoted(s: string): string {
  return s
    .replaceAll("\\", "\\\\")
    .replaceAll("'", "\\'")
    .replaceAll("\n", "\\n")
    .replaceAll("\r", "\\r")
    .replaceAll("\t", "\\t")
    .replaceAll("\u2028", "\\u2028")
    .replaceAll("\u2029", "\\u2029");
}

/**
 * Renders a string as a complete single-quoted Vega expression literal.
 */
export function vegaSingleQuoted(s: string): string {
  return `'${escapeVegaSingleQuoted(s)}'`;
}

/**
 * Renders a `datum[...]` accessor for a field name inside a Vega expression.
 */
export function vegaDatum(field: string): string {
  return `datum[${vegaSingleQuoted(field)}]`;
}

/**
 * Prepares a title for a Vega-Lite `tooltip` encoding entry. Vega-Lite splices
 * each title raw into one object-literal expression and escapes only double
 * quotes (see tooltipData in vega-lite), so backslashes and line breaks
 * must be escaped here and double quotes must be left alone.
 */
export function escapeVegaLiteTooltipTitle(title: string): string {
  return title
    .replaceAll("\\", "\\\\")
    .replaceAll("\n", "\\n")
    .replaceAll("\r", "\\r")
    .replaceAll("\t", "\\t")
    .replaceAll("\u2028", "\\u2028")
    .replaceAll("\u2029", "\\u2029");
}

export function escapeVegaString(str: string): string {
  return str
    .replaceAll(".", "\\.")
    .replaceAll("'", "\\'")
    .replaceAll('"', '\\"')
    .replaceAll("[", "\\[")
    .replaceAll("]", "\\]")
    .replaceAll("\n", "\\n")
    .replaceAll("\r", "\\r")
    .replaceAll("\t", "\\t")
    .replaceAll("\b", "\\b");
}

export function unescapeVegaString(str: string): string {
  // this function is risky:
  // it should only be applied where the above has been applied first
  // otherwise it'll unwrap some other, previous wrapping
  return str
    .replaceAll("\\.", ".")
    .replaceAll("\\'", "'")
    .replaceAll('\\"', '"')
    .replaceAll("\\[", "[")
    .replaceAll("\\]", "]")
    .replaceAll("\\n", "\n")
    .replaceAll("\\r", "\r")
    .replaceAll("\\t", "\t")
    .replaceAll("\\b", "\b");
}

/**
 * Guards strings for Vega given outstanding bug referenced in docstring for
 * patchVegaEscapeIssue below.
 */
const JSONEscapeString = (str: string): string => {
  const strJSON = JSON.stringify(str);
  return strJSON.substr(1, strJSON.length - 2);
};

/**
 * For some reason in addition to normal JSON escaping,
 * fields also need to escape single quotes, periods, brackets while titles don't.
 *
 * This results in an ugly default title, but its fixible if the
 * user provides a better title
 *
 * See https://github.com/vega/vega-lite/issues/7790
 */
const escapeFieldString = (str: string): string => {
  return escapeVegaString(unescapeVegaString(JSONEscapeString(str)));
};

/**
 * Vega breaks if you populate some fields on an encoding channel that contains
 * quotes, and potentially other characteres necessitating escape. Rather than
 * botch these values as we persist them to the back end, we patch them in here
 * prior to vegaEmbed so we can simply remove this abomination once the library
 * has been updated.
 *
 * See:
 * - https://github.com/vega/vega-lite/issues/7460
 * - https://github.com/vega/vega-lite/issues/7790
 *
 * TODO: Remove this as soon as the above are resolved.
 */
export const patchVegaEscapeIssue = <T extends VegaLiteSpec | VegaSpec>(
  spec: T,
): T => {
  // for now, we only patch things in our expected Vega-Lite spec format from chart cells
  // However, let other more complex vega specs through in case they come from using the `HexChart()` API
  if (!isVegaLiteSpec(spec) || spec.layer == null) {
    return spec;
  }

  return {
    ...spec,
    layer: spec.layer.map((layer) => {
      return {
        ...layer,
        encoding: Object.entries(layer?.encoding ?? {})
          .map(([key, encodingDef]) => {
            if (isFieldDef(encodingDef)) {
              return [
                key,
                {
                  ...encodingDef,
                  ...(encodingDef.field != null &&
                  typeof encodingDef.field === "string"
                    ? {
                        field: escapeFieldString(encodingDef.field),
                      }
                    : {}),
                  ...(encodingDef.title != null &&
                  typeof encodingDef.title === "string"
                    ? {
                        title: JSONEscapeString(encodingDef.title),
                      }
                    : {}),
                },
              ];
            } else {
              return [key, encodingDef];
            }
          })
          // vega types make this _really_ hard to do in a typesafe way
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .reduce<any>((acc, [key, encodingDef]) => {
            acc[key as string] = encodingDef;
            return acc;
          }, {}),
      };
    }),
  };
};
